import { NotificationsContext, VibratoClientContext } from "@/components/dashboard/contexts"
import { Call, PrankCallCategory, PrankCallType, PrankCallVoice } from "@/lib/vibrato-api"
import { Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import { Phone, Play } from "iconoir-react"
import moment from "moment"
import React, { useContext, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

interface PrankCallConfigProps {
  onComplete: (call: Call) => void
}

export default function PrankCall({ onComplete }: PrankCallConfigProps): React.JSX.Element {
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [voices, setVoices] = useState<PrankCallVoice[]>([])
  const [voicesByCategory, setVoicesByCategory] = useState<Record<string, PrankCallVoice[]>>({})
  const [categories, setCategories] = useState<Record<string, PrankCallCategory>>({})
  const [selectedVoice, setSelectedVoice] = useState<PrankCallVoice | null>(null)
  const [selectedCallType, setSelectedCallType] = useState<PrankCallType | null>(null)
  const [recipientName, setRecipientName] = useState<string>("")
  const [recipientPhone, setRecipientPhone] = useState<string>("")
  const [countryCode, setCountryCode] = useState<string>("1")
  const [callLocale, setCallLocale] = useState<string>("en-us")
  const [showScheduleModal, setShowScheduleModal] = useState<boolean>(false)
  const [scheduledAt, setScheduledAt] = useState<string | null>(null)
  const [isPlacingCall, setIsPlacingCall] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [playingVoiceSlug, setPlayingVoiceSlug] = useState<string | null>(null)
  const [loadingVoiceSlug, setLoadingVoiceSlug] = useState<string | null>(null)
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null)
  const [, setSignatureCallTypeMap] = useState<Record<string, string[]>>({})

  const vibratoClient = useContext(VibratoClientContext)
  const notifications = useContext(NotificationsContext)
  const navigate = useNavigate()

  // Create an anchor ref that we'll scroll into view on slide change
  const topAnchorRef = useRef<HTMLDivElement>(null)

  // When currentStep changes, scroll the top anchor into view
  useEffect(() => {
    topAnchorRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [currentStep])

  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const voiceData = await vibratoClient.getPrankCallVoices()
        setVoices(voiceData)

        // Create categories lookup and index voices by category
        const categoriesLookup: Record<string, PrankCallCategory> = {}
        const categorizedVoices = voiceData.reduce((acc: Record<string, PrankCallVoice[]>, voice: PrankCallVoice) => {
          voice.categories.forEach((category: PrankCallCategory) => {
            categoriesLookup[category.slug] = category
            if (!acc[category.slug]) {
              acc[category.slug] = []
            }
            acc[category.slug].push(voice)
          })
          return acc
        }, {})

        // Sort voices by name within each category
        Object.keys(categorizedVoices).forEach((category) => {
          categorizedVoices[category].sort((a: PrankCallVoice, b: PrankCallVoice) => a.name.localeCompare(b.name))
        })

        setCategories(categoriesLookup)
        setVoicesByCategory(categorizedVoices)
        // setSelectedCategory("all")

        // Create a mapping from voice slug to signature call type slugs
        const signatureMap = voiceData.reduce(
          (acc: Record<string, string[]>, voice: PrankCallVoice) => {
            acc[voice.slug] = voice.signature_call_types?.map((callType) => callType.slug)
            return acc
          },
          {} as Record<string, string[]>
        )

        setSignatureCallTypeMap(signatureMap)
      } catch (error) {
        console.error("Error fetching voices:", error)
        setError("Failed to load voices")
      }
    }

    fetchVoices()
  }, [vibratoClient])

  const playVoiceDemo = async (voice: PrankCallVoice) => {
    try {
      setLoadingVoiceSlug(voice.slug)
      const demoUrl = await vibratoClient.getVoiceDemo(voice.slug)
      if (audioElement) {
        audioElement.pause()
      }

      const audio = new Audio(demoUrl.audioUrl)
      setAudioElement(audio)

      audio.onended = () => {
        setPlayingVoiceSlug(null)
      }

      await audio.play()
      setPlayingVoiceSlug(voice.slug)
    } catch (error) {
      console.error("Error playing voice demo:", error)
      setError("Failed to play voice demo")
    } finally {
      setLoadingVoiceSlug(null)
    }
  }

  const stopVoiceDemo = () => {
    if (audioElement) {
      audioElement.pause()
      setPlayingVoiceSlug(null)
    }
  }

  const handleNext = async () => {
    if (validateCurrentStep()) {
      if (currentStep === 2) {
        if (!selectedVoice || !selectedCallType) {
          setError("Please select a voice and call type.")
          return
        }
        try {
          const call = await vibratoClient.createCall({
            call_type: "phone",
            task_slug: "prank_call",
            task_recipient_slug: "anyone",
            retries: 0,
            retry_delay_seconds: 0,
            task_parameters: {
              "voice-slug": selectedVoice!.slug,
              "call-type-slug": selectedCallType!.slug
            }
          })

          console.log("Created call", call)
          navigate(`/calls/${call.id}/edit/`)
        } catch (error) {
          console.error("Error creating call:", error)
          setError("Failed to create initial call")
        }
      } else {
        setCurrentStep(currentStep + 1)
      }
    }
  }

  const handleBack = () => {
    setCurrentStep(currentStep - 1)
  }

  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        if (!selectedVoice) {
          setError("Please select a voice.")
          return false
        }
        break
      case 2:
        if (!selectedCallType) {
          setError("Please select a call type.")
          return false
        }
        break
      case 3:
        if (!recipientName || !recipientPhone) {
          setError("Please fill in all recipient details.")
          return false
        }
        break
    }
    setError(null)
    return true
  }

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return

    setIsPlacingCall(true)
    try {
      const call = await vibratoClient.createPrankCall({
        voiceSlug: selectedVoice!.slug,
        callTypeSlug: selectedCallType!.slug,
        recipientName,
        recipientPhoneNumber: recipientPhone.replace(/\D/g, ""),
        recipientCountryCode: countryCode,
        callLocale,
        scheduledAt
      })

      notifications.addNotification({
        type: "success",
        message: "Call configured successfully!",
        timeout: 5000
      })

      onComplete(call)
    } catch (error) {
      console.error("Error creating call:", error)
      setError("Failed to create call")
    } finally {
      setIsPlacingCall(false)
    }
  }

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Step 1: Select Voice</h2>
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1 rounded-full text-sm ${
                  !selectedCategory ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All Voices
              </button>
              {Object.values(categories)
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((category) => (
                  <button
                    key={category.slug}
                    onClick={() => setSelectedCategory(category.slug)}
                    className={`px-3 py-1 rounded-full text-sm ${
                      selectedCategory === category.slug
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
            </div>
            <p className="text-gray-600 mb-4">Choose a voice:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {(selectedCategory ? voicesByCategory[selectedCategory] : voices).map((voice) => (
                <div
                  key={voice.slug}
                  className={`group flex flex-col relative overflow-hidden rounded-xl border-2 transition-all duration-200 ${
                    selectedVoice?.slug === voice.slug
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-blue-300 hover:shadow-lg"
                  }`}
                >
                  <div className="p-6 grow">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-xl font-semibold">{voice.name}</h3>
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            if (playingVoiceSlug === voice.slug) {
                              stopVoiceDemo()
                            } else {
                              playVoiceDemo(voice)
                            }
                          }}
                          className="ml-4 p-3 rounded-full hover:bg-blue-100 transition-colors bg-blue-50 group-hover:bg-blue-100"
                          title={playingVoiceSlug === voice.slug ? "Stop preview" : "Preview voice"}
                        >
                          {loadingVoiceSlug === voice.slug ? (
                            <svg
                              className="animate-spin h-6 w-6 text-blue-600"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                          ) : playingVoiceSlug === voice.slug ? (
                            <svg
                              className="h-6 w-6 text-blue-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <rect x="6" y="4" width="4" height="16" fill="currentColor" />
                              <rect x="14" y="4" width="4" height="16" fill="currentColor" />
                            </svg>
                          ) : (
                            <Play className="h-6 w-6 text-blue-600" />
                          )}
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {voice.categories
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((category) => (
                            <span
                              key={category.slug}
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setSelectedCategory(category.slug)
                              }}
                              className="px-2 py-1 text-xs rounded-full bg-gray-50 text-gray-500 cursor-pointer hover:bg-gray-200 hover:text-gray-700 transition-colors"
                            >
                              {category.name}
                            </span>
                          ))}
                      </div>
                      <p className="text-gray-600 leading-relaxed max-w-prose">{voice.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedVoice(voice)}
                    className={`w-full p-4 text-center transition-colors ${
                      selectedVoice?.slug === voice.slug
                        ? "bg-blue-500 text-white"
                        : "bg-gray-50 text-gray-700 hover:bg-blue-500 hover:text-white"
                    }`}
                  >
                    {selectedVoice?.slug === voice.slug ? "Selected" : "Select Voice"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )

      case 2:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Step 2: Select Call Type</h2>
            <p className="text-gray-600">Choose a call type:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {selectedVoice?.call_types.map((callType) => (
                <div
                  key={callType.slug}
                  className={`group flex flex-col relative overflow-hidden rounded-xl border-2 transition-all duration-200 ${
                    selectedCallType?.slug === callType.slug
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 hover:border-blue-300 hover:shadow-lg"
                  }`}
                >
                  <div className="p-6 grow">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold mb-3">{callType.name}</h3>
                      <p className="text-gray-600 leading-relaxed max-w-prose">{callType.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedCallType(callType)}
                    className={`w-full p-4 text-center transition-colors ${
                      selectedCallType?.slug === callType.slug
                        ? "bg-blue-500 text-white"
                        : "bg-gray-50 text-gray-700 hover:bg-blue-500 hover:text-white"
                    }`}
                  >
                    {selectedCallType?.slug === callType.slug ? "Selected" : "Select Call Type"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )

      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Step 3: Recipient Details</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700">Recipient Name</label>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter recipient's name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone Number</label>
              <div className="flex gap-2">
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="mt-1 block w-1/3 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  {/* Add country code options here */}
                </select>
                <input
                  type="tel"
                  value={recipientPhone}
                  onChange={(e) => setRecipientPhone(e.target.value)}
                  className="mt-1 block w-2/3 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="(123) 456-7890"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Call Language</label>
              <select
                value={callLocale}
                onChange={(e) => setCallLocale(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                {/* Add call language options here */}
              </select>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="flex flex-col w-full relative">
      {/* Top anchor element to scroll into view */}
      <div ref={topAnchorRef} />
      {error && (
        <div className="sticky top-0 left-0 right-0 z-50 flex items-center justify-between p-4 bg-red-50 border-b border-red-200">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-red-700">{error}</span>
          </div>
          <button onClick={() => setError(null)} className="ml-4 text-red-600 hover:text-red-800 focus:outline-none">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      <div className="w-full p-6 relative pb-24">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold">Configure Prank Call</h1>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <span className={currentStep >= 1 ? "text-blue-600" : ""}>Select Voice</span>
              <span>→</span>
              <span className={currentStep >= 2 ? "text-blue-600" : ""}>Call Type</span>
            </div>
          </div>

          {/* Add prominent disclaimer */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <div className="flex">
              <div className="shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Important Disclaimer</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    This is an AI-generated prank call service. The voices used are artificial recreations and are not
                    real characters. These calls are intended for entertainment purposes only and do not represent
                    endorsements by or actual participation of the impersonated individuals.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="font-thin">
            <p>
              Create entertaining AI-powered calls using our library of fictional characters. Simply select a voice,
              choose from their signature call types, and provide the recipient's details. Our AI handles the
              conversation naturally while ensuring appropriate boundaries.
            </p>
          </div>

          <div className="flex flex-wrap gap-4 mb-6">
            {selectedVoice && (
              <div className="flex items-center bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 mt-6">
                <div className="mr-3">
                  <div className="text-xs text-blue-600 font-medium uppercase tracking-wide">Selected Voice</div>
                  <div className="text-sm font-semibold text-gray-900">{selectedVoice.name}</div>
                </div>
                <button
                  onClick={() => {
                    setSelectedVoice(null)
                    setCurrentStep(1)
                  }}
                  className="ml-2 p-1 hover:bg-blue-100 rounded-full"
                >
                  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {selectedCallType && (
              <div className="flex items-center bg-green-50 border border-green-200 rounded-lg px-4 py-2 mt-6">
                <div className="mr-3">
                  <div className="text-xs text-green-600 font-medium uppercase tracking-wide">Selected Call Type</div>
                  <div className="text-sm font-semibold text-gray-900">{selectedCallType.name}</div>
                </div>
                <button
                  onClick={() => {
                    setSelectedCallType(null)
                    setCurrentStep(2)
                  }}
                  className="ml-2 p-1 hover:bg-green-100 rounded-full"
                >
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {renderStep()}

        <div className="sticky bottom-8 z-50 w-full flex justify-center pt-6">
          <div className={currentStep === 1 || currentStep === 2 ? "" : "ml-auto"}>
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`px-6 py-3 border border-gray-300 rounded-lg shadow-lg text-base font-medium text-gray-700 bg-white transition-all mr-4 ${
                currentStep === 1 ? "opacity-90 cursor-not-allowed" : "hover:bg-gray-50"
              } ${
                (currentStep === 1 && selectedVoice) || (currentStep === 2 && selectedCallType)
                  ? "scale-105 hover:scale-110"
                  : ""
              }`}
            >
              Back
            </button>
            {currentStep < 3 ? (
              <button
                onClick={handleNext}
                className={`px-6 py-3 border border-transparent rounded-lg shadow-lg text-base font-medium text-white bg-blue-600 hover:bg-blue-700 transition-all ${
                  (currentStep === 1 && selectedVoice) || (currentStep === 2 && selectedCallType)
                    ? "scale-105 hover:scale-110"
                    : ""
                }`}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isPlacingCall}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
              >
                {isPlacingCall ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Configuring...
                  </>
                ) : (
                  <>
                    <Phone className="mr-2 h-4 w-4" />
                    Configure Call
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <Modal show={showScheduleModal} onClose={() => setShowScheduleModal(false)} className="pt-safe">
          <ModalHeader className="border-b border-gray-200">Schedule Call</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Select when you want the call to be placed:</p>
              <input
                type="datetime-local"
                value={scheduledAt || ""}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                min={moment().format("YYYY-MM-DDTHH:mm")}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <button
              onClick={() => setShowScheduleModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (scheduledAt) {
                  setShowScheduleModal(false)
                }
              }}
              className="ml-3 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Confirm
            </button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  )
}
